import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@app/component/translation/Translation';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useForm } from 'react-hook-form';
import ResetPasswordInput from '@app/model/profile/input/ResetPasswordInput';
import ProfileService from '@app/model/profile/service/ProfileService';
import FormInputPassword from '@app/component/form/input/text/FormInputPassword';


const ResetPasswordPage = React.memo(() => {

	const { key } = useParams();

	const { t } = useTranslation();
	const toaster = useToaster();
	const form = useForm<ResetPasswordInput>();
	const navigate = useNavigate();

	useEffect(() => {
		reset();
	}, []);

	const reset = () => {
		form.reset({
			key: key,
			newPassword: '',
			repeatNewPassword: '',
		});
	};

	const onSubmit = (input: ResetPasswordInput) => {
		ProfileService.resetPassword(input)
			.then(() => {
				toaster.success('Reset Password', 'Password changed successfully!');
				reset();
				navigate('/sign-in');
			}).catch(error => {
			if (error === 'server.exception.profile.RESET_PASSWORD_REQUEST_KEY_EXPIRED') {
				toaster.error('Reset Password', t(error));
			} else if (error === 'server.exception.profile.PASSWORD_DOES_NOT_VALIDATE') {
				form.setError('newPassword', { message: t(error) });
			} else if (error === 'server.exception.profile.PASSWORDS_DO_NOT_MATCH') {
				form.setError('newPassword', { message: t(error) });
				form.setError('repeatNewPassword', { message: t(error) });
			}

		});
	};

	return <div className="min-h-full bg-white">
		<div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
			<div className="flex flex-1 flex-col justify-center px-6 py-12">
				<div className="mx-auto w-full max-w-sm lg:w-96">
					<div>
						<img
							className="h-10 w-auto"
							src="/assets/img/brand/logo-icon.png"
							alt="RoomPulse"
						/>
						<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
							Forgot Password
						</h2>
						<p className="mt-2 text-sm leading-6 text-gray-500">
							Already registered?{' '}
							<Link to="/sign-in" className="font-semibold text-blue-600 hover:text-blue-500"
										title="Click to sign in to your account">Go back to sign in</Link>
						</p>
					</div>

					<div className="mt-10">
						<div>
							<form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
								<div className="mt-6 flex flex-col lg:flex-row">
									<div className="flex-grow space-y-6">
										<input type="hidden" {...form.register('key')} />
										<div>
											<FormInputPassword name="newPassword"
																				 label="New Password"
																				 labelHint="Use more than 8 characters"
																				 form={form} />
										</div>
										<div>
											<FormInputPassword name="repeatNewPassword" label="Repeat New Password" form={form} />
										</div>
									</div>
								</div>

								<div>
									<button
										type="submit"
										className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
									>
										Set New Password
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
			<div className="relative hidden flex-1 lg:block">
				<img
					className="absolute inset-0 h-full w-full object-cover"
					src="https://images.unsplash.com/photo-1625998464641-17bf5a53b9b7?q=80&w=2576&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt=""
				/>
			</div>
		</div>
	</div>;
});

export default ResetPasswordPage;